import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import { formatPrice, formatUnitPrice } from '../Prices/priceLookupShared';
import { AvailabilityPill, CategoryPill } from '../../../shared/Pill';
import { formatDate } from '../../../shared/tenantClock';
import { businessNumberLabel, formatBusinessNumber } from '../../../shared/businessNumber';
import { formatNumber, formatMoney } from '../../../shared/numberFormat';
import { discountLabel } from '../shared/OrderDiscount';
import { basisOf, shownUnitPrice, shownLineAmount, documentTotals, netOfCancelled } from '../../../shared/pricingBasis';
import { viewSubtotal } from '../../../shared/gstView';
import { SALES_ORDER_STATUS_LABELS as STATUS_LABELS } from '../../../shared/salesOrderStatus';
import TaxBasisLine from '../../../shared/TaxBasisLine';
import { PaymentDetailsBox } from '../../../shared/paymentOptions';

// Same multi-line letterhead address helpers as QuoteReportView.js, kept as
// local copies here for the same reason that file gives (a print-report
// concern reading company_settings/address shapes, not a settings-form or
// shared-lookup concern).
function companyAddressLines(data) {
  if (!data) return [];
  const unit = data.address_unit ? `${data.address_unit}, ` : '';
  const line1 = `${unit}${data.address_street || ''}`.trim();
  const line2 = [data.address_district, data.address_city, data.address_state, data.address_postcode].filter(Boolean).join(' ');
  // Country is the last line (#165), left off when it's the tenant's own
  // country (see foreignCountry).
  return [line1, line2, foreignCountry(data.country, data.country)].filter(Boolean);
}

// A country prints only when it differs from the tenant's own, so a
// domestic Sales Order doesn't spend a line on "Australia" in both blocks.
function foreignCountry(country, homeCountry) {
  if (!country) return null;
  return homeCountry && String(country).trim().toLowerCase() === String(homeCountry).trim().toLowerCase() ? null : country;
}

// Who the delivery is for when the address belongs to an Individual linked
// to the Customer (an employee) rather than the Customer itself — see
// deliveryAddressService.js.
function attentionOf(doc) {
  const owner = doc.address?.commercial_entity;
  return owner && String(owner.id) !== String(doc.customer?.id) ? owner.name : null;
}

function customerAddressLines(a, homeCountry) {
  if (!a) return [];
  const line1 = [a.line1, a.line2].filter(Boolean).join(', ');
  const line2 = [a.suburb, a.state, a.postcode].filter(Boolean).join(' ');
  // Country is the last line (#165), left off when it's the tenant's own.
  return [line1, line2, foreignCountry(a.country, homeCountry)].filter(Boolean);
}

function MetaRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '1px 0' }}>
      <span style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}

// The priced Sales Order print (issue #293) — the Customer's record of what
// they ordered and at what price, laid out like the Quote print
// (QuoteReportView.js). Titled "Sales Order", never an invoice: invoicing
// stays in QuickBooks. The Customer sees it, so nothing internal prints —
// no Staff Comment, no Hold, no picking/packing detail (that's the Pick
// Slip, PickSlipReportView.js).
function SalesOrderReportView({ salesOrderId, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companySettings, setCompanySettings] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`/api/sales-orders/${salesOrderId}`)
      .then((res) => setOrder(res.data.data))
      .catch((err) => setError(err.response?.data?.message || 'Failed to load Sales Order'))
      .finally(() => setLoading(false));
    axios.get('/api/company-settings')
      .then((res) => setCompanySettings(res.data.data || null))
      .catch(() => setCompanySettings(null));
  }, [salesOrderId]);

  const lines = order?.lines || [];
  const statusLabel = order ? (STATUS_LABELS[order.status] || order.status) : '';

  // Same Pricing Basis rules as the Sales Order screen and the Quote print
  // (shared/pricingBasis.js) — one figure per row, inc GST for Retail, ex
  // GST for Business. The screen's Ex GST / Inc GST switch is a staff view
  // only and never changes the Customer's copy.
  const taxHandling = order?.customer?.tax_handling_type;
  const gstApplicable = !!taxHandling?.tax_code && Number(taxHandling.tax_code.rate) > 0;
  const basis = basisOf(taxHandling);
  const basisSuffix = gstApplicable ? (basis.retail ? ' (inc GST)' : ' (ex GST)') : '';
  const basisLine = <TaxBasisLine>{basisSuffix.slice(2, -1)}</TaxBasisLine>;
  // A cancelled backorder's share counts in neither the Amount nor the
  // totals (netOfCancelled).
  const formLine = (line) => netOfCancelled({
    unitPrice: line.unit_price,
    unitPriceInc: line.unit_price_inc,
    extendedPrice: line.extended_price,
    gstAmount: line.gst_amount,
    extendedPriceIncGst: line.extended_price_inc_gst,
    qty: line.qty,
    qtyCancelled: line.qty_cancelled,
  });
  const linePrice = (line) => shownUnitPrice(formLine(line), line.gst_rate ?? taxHandling?.tax_code?.rate, basis);
  const lineAmount = (line) => shownLineAmount(formLine(line), basis);

  const soldUomOf = (line) => (line.product?.uoms || []).find((pu) => String(pu.uom_id) === String(line.uom_id));

  // A pack line's price per base unit, under Unit Price — same figure as
  // SalesOrderPage.js's own baseUnitPriceLabel.
  const baseUnitPriceLabel = (line) => {
    const price = linePrice(line);
    if (price === null || price === undefined) return null;
    const soldUom = soldUomOf(line);
    const baseUom = line.product?.base_uom;
    const rate = Number(soldUom?.conversion_rate);
    if (!soldUom || !baseUom || rate === 1 || !(rate > 0)) return null;
    const perBase = price / rate;
    const decimals = Number(perBase.toFixed(2)) === Number(perBase.toFixed(4)) ? 2 : 4;
    return `${formatMoney(perBase, { decimals })} / ${baseUom.code || baseUom.name}`;
  };

  // Base-unit qty under UoM for a pack line, same as the Quote print.
  const baseUomNote = (line) => {
    const soldUom = soldUomOf(line);
    const baseUom = line.product?.base_uom;
    if (!soldUom || !baseUom || Number(soldUom.conversion_rate) === 1) return null;
    const baseQty = Number(line.qty || 0) * Number(soldUom.conversion_rate);
    return `${formatNumber(baseQty, { maxDecimals: 2 })} ${baseUom.code || baseUom.name}`;
  };

  // Shipped/Backordered columns only while part of the order is still to
  // come: something has shipped and something is still backordered (issue
  // #453). A cancelled remainder isn't still to come.
  const backorderedQty = (line) => Math.max(0, Number(line.qty || 0) - Number(line.qty_shipped || 0) - Number(line.qty_cancelled || 0));
  const showShipment = lines.some((l) => Number(l.qty_shipped) > 0) && lines.some((l) => backorderedQty(l) > 0);
  const formatQty = (n) => formatNumber(Number(n || 0), { maxDecimals: 4 });

  // Freight left blank is "to be added" (issue #351), and the total then leaves it
  // out — same as the portal (portalOrderService.js's orderTotals).
  const gstRate = Number(taxHandling?.tax_code?.rate) || 0;
  const shippingKnown = order?.shipping_cost !== null && order?.shipping_cost !== undefined;
  const selectedOptions = order?.selected_options || [];
  const shippingCostEx = Number(order?.shipping_cost || 0);
  const optionsCostEx = selectedOptions.reduce((sum, o) => sum + (Number(o.cost) || 0), 0);
  const totals = documentTotals({ lines: lines.map(formLine), shippingEx: shippingCostEx, optionsEx: optionsCostEx, gstRate: gstApplicable ? gstRate : 0, basis, discount: order?.discount_type ? { type: order.discount_type, value: order.discount_value } : null });
  const showShippingDisclaimer = shippingKnown && !!(order?.shipping_service?.disclaimer && order?.shipping_service?.show_disclaimer);
  const showPendingCostDisclaimer = !shippingKnown && !!order?.shipping_service?.cost_confirmed_later && !!order?.shipping_service?.pending_cost_disclaimer;

  // Total's qualifiers sit small under it rather than lengthening the label.
  const totalNote = [gstApplicable && 'inc GST', !shippingKnown && 'ex shipping'].filter(Boolean).join(', ');
  const totalRows = [
    // Same rows for Business and Retail, as on the screen.
    (gstApplicable || !basis.retail) && { key: 'subtotal', label: `Subtotal${basisSuffix}`, value: formatPrice(viewSubtotal(lines.map(formLine), gstApplicable && basis.retail)) },
    totals.discount > 0 && { key: 'discount', label: `${discountLabel(order?.discount_type, order?.discount_value)}${basisSuffix}`, value: `-${formatPrice(totals.discount)}` },
    shippingKnown ? {
      key: 'shipping',
      label: `Shipping${gstApplicable ? ' (inc GST)' : ''}`,
      value: <>{formatPrice(totals.shipping)}{showShippingDisclaimer && <sup style={{ marginLeft: 2 }}>1</sup>}</>,
    } : {
      key: 'shipping',
      label: <>Shipping{showPendingCostDisclaimer && <sup style={{ marginLeft: 2 }}>1</sup>}</>,
      value: 'To be added',
      valueStyle: { fontStyle: 'italic', color: '#666' },
    },
    // Options (e.g. insurance) get their own line, labelled with the
    // Option itself rather than listed in the header.
    selectedOptions.length > 0 && { key: 'options', label: `${selectedOptions.map((o) => o.shipping_option.description).join(', ')}${gstApplicable ? ' (inc GST)' : ''}`, value: formatPrice(totals.options) },
    gstApplicable && { key: 'gst', label: 'GST', value: formatPrice(totals.gst) },
    { key: 'total', label: 'Total', value: formatPrice(totals.total), total: true },
    // Spans the label and value columns on one unwrapped line, so it can't
    // wrap down the narrow label column and push onto a second page.
    totalNote && { key: 'total-note', label: totalNote, span: true, muted: true },
  ].filter(Boolean);

  // Totals labels span the three columns left of Amount so a label like
  // "Subtotal (ex GST)" stays on one line; the comments/notes cell takes
  // the columns left of that.
  // Totals sit under Amount, so Shipped/Backordered (after it) get an empty
  // cell of their own.
  const labelCols = 3;
  const leadingCols = 5 - labelCols;
  const trailingCols = showShipment ? 2 : 0;

  return (
    <ReportShell
      onClose={onClose}
      printDisabled={loading || !order}
      hideRunningHeader
      hideConfidentialityNote
      // Status as a pill beside the title, which is how a Draft reads as a
      // Draft — no separate banner, to keep the page compact.
      title={order ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
          Sales Order {order.display_number}
          <CategoryPill value={statusLabel} />
        </span>
      ) : undefined}
      footerNote={order ? `${order.customer?.name} · ${statusLabel} · ${formatReportTimestamp()}` : undefined}
    >
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {order && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, marginBottom: 4, fontSize: 12 }}>
            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>SUPPLIED BY</div>
              <div style={{ fontWeight: 600 }}>{companySettings?.company_name || '—'}</div>
              {companySettings?.abn && <div>ABN {companySettings.abn}</div>}
              {companyAddressLines(companySettings).map((l) => <div key={l}>{l}</div>)}
              {companySettings?.phone_number && <div>{companySettings.phone_number}</div>}
              {companySettings?.email && <div>{companySettings.email}</div>}
            </div>

            <div style={{ maxWidth: 240 }}>
              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, marginBottom: 4 }}>SUPPLIED TO</div>
              <div style={{ fontWeight: 600 }}>{order.customer?.name || '—'}</div>
              {order.customer?.business_number && <div>{businessNumberLabel(order.customer.business_number_type)} {formatBusinessNumber(order.customer.business_number_type, order.customer.business_number)}</div>}
              {attentionOf(order) && <div>Attn: {attentionOf(order)}</div>}
              {customerAddressLines(order.address, companySettings?.country).map((l) => <div key={l}>{l}</div>)}
            </div>

            <div style={{ minWidth: 180 }}>
              <MetaRow label="Order Date" value={formatDate(order.order_date)} />
              {order.po_reference && <MetaRow label="PO Reference" value={order.po_reference} />}
              <MetaRow label="Terms" value={order.customer?.payment_term?.name || '—'} />
              {(order.carrier || order.shipping_service) && <MetaRow label="Shipping" value={[order.carrier?.name, order.shipping_service?.description].filter(Boolean).join(' ')} />}
            </div>
          </div>

          {lines.length === 0 ? (
            <div style={{ color: '#888' }}>No lines.</div>
          ) : (
            // Totals rows share the line table's columns, same reasoning as
            // QuoteReportView.js — the only way the right edges reliably
            // line up between screen and print.
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
              <colgroup>
                <col style={{ width: showShipment ? '13%' : '16%' }} />
                <col style={{ width: showShipment ? '29%' : '38%' }} />
                <col style={{ width: showShipment ? '10%' : '12%' }} />
                <col style={{ width: showShipment ? '7%' : '9%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: showShipment ? '13%' : '14%' }} />
                {showShipment && <col style={{ width: '8%' }} />}
                {showShipment && <col style={{ width: '9%' }} />}
              </colgroup>
              <thead style={{ fontSize: reportStyle.tableFontSize }}>
                <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                  <th style={{ padding: '3px 8px 3px 0' }}>SKU</th>
                  <th style={{ padding: '3px 8px 3px 0' }}>Product</th>
                  <th style={{ padding: '3px 8px 3px 0' }}>UoM</th>
                  <th style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>Qty</th>
                  <th style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>Unit Price{basisLine}</th>
                  <th style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>Amount{basisLine}</th>
                  {showShipment && <th style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>Shipped</th>}
                  {showShipment && <th style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>Back-<br />ordered</th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => {
                  // Any cancelled qty gets an "N cancelled" note under Qty; a
                  // line cancelled in full (nothing shipped, nothing owed) is
                  // also struck through, same as SalesOrderPage.js.
                  const cancelled = Number(line.qty_cancelled || 0);
                  const struck = cancelled > 0 && !Number(line.qty_shipped) && backorderedQty(line) === 0
                    ? { textDecoration: 'line-through', color: '#999' }
                    : {};
                  return (
                  <tr key={line.id} style={{ borderBottom: '1px solid #ddd' }}>
                    <td style={{ padding: '3px 8px 3px 0', overflowWrap: 'break-word', ...struck }}>{line.product?.sku}</td>
                    <td style={{ padding: '3px 8px 3px 0', ...struck }}>
                      {line.product?.name}
                      {line.product?.availability_badge && (
                        <>
                          {' '}
                          <AvailabilityPill
                            badge={line.product.availability_badge}
                            date={line.product.availability_date}
                            color={line.product.availability_badge_color}
                          />
                        </>
                      )}
                    </td>
                    <td style={{ padding: '3px 8px 3px 0', ...struck }}>
                      {line.uom?.name}
                      {baseUomNote(line) && (
                        <div style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>{baseUomNote(line)}</div>
                      )}
                    </td>
                    <td style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>
                      <span style={struck}>{formatQty(line.qty)}</span>
                      {cancelled > 0 && <div style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>{formatQty(cancelled)} cancelled</div>}
                    </td>
                    <td style={{ padding: '3px 8px 3px 0', textAlign: 'right', ...struck }}>
                      {formatUnitPrice(linePrice(line))}
                      {baseUnitPriceLabel(line) && (
                        <div style={{ fontSize: 11, color: '#666', whiteSpace: 'nowrap' }}>{baseUnitPriceLabel(line)}</div>
                      )}
                    </td>
                    <td style={{ padding: '3px 8px 3px 0', textAlign: 'right', ...struck }}>
                      {formatPrice(lineAmount(line))}
                    </td>
                    {/* A zero Shipped or Backordered prints blank. */}
                    {showShipment && <td style={{ padding: '3px 8px 3px 0', textAlign: 'right' }}>{Number(line.qty_shipped) ? formatQty(line.qty_shipped) : ''}</td>}
                    {showShipment && (
                      <td style={{ padding: '3px 8px 3px 0', textAlign: 'right', fontWeight: backorderedQty(line) > 0 ? 600 : 400 }}>
                        {backorderedQty(line) > 0 ? formatQty(backorderedQty(line)) : ''}
                      </td>
                    )}
                  </tr>
                  );
                })}
                {totalRows.map((row, i) => {
                  const first = i === 0;
                  const top = first ? '8px' : row.total ? '4px' : '2px';
                  const bottom = row.total ? '0' : '2px';
                  const rowStyle = {
                    textAlign: 'right',
                    ...(row.total ? { fontWeight: 700, fontSize: 16, borderTop: reportStyle.tableHeaderBorder } : {}),
                    ...(row.muted ? { fontSize: 12, color: '#666' } : {}),
                  };
                  return (
                    <tr key={row.key} style={{ breakInside: 'avoid' }}>
                      {first && (
                        <td rowSpan={totalRows.length} colSpan={leadingCols} style={{ verticalAlign: 'top', padding: '8px 16px 0 0', fontSize: 12, color: '#666', borderTop: '1px solid #ddd' }}>
                          {/* customer_comment only — staff_comment never prints. */}
                          {order.customer_comment && (
                            <div style={{ marginBottom: 12, fontSize: 13, color: '#1a1a1a' }}>
                              <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Comments</div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{order.customer_comment}</div>
                            </div>
                          )}
                          {(showShippingDisclaimer || showPendingCostDisclaimer) && (
                            <div style={{ color: '#888', fontSize: 11, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 4 }}>Notes</div>
                          )}
                          {showShippingDisclaimer && <div><sup>1</sup> {order.shipping_service.disclaimer}</div>}
                          {showPendingCostDisclaimer && <div><sup>1</sup> {order.shipping_service.pending_cost_disclaimer}</div>}
                        </td>
                      )}
                      {row.span ? (
                        <td colSpan={labelCols + 1} style={{ padding: '0', whiteSpace: 'nowrap', ...rowStyle, fontSize: 11 }}>{row.label}</td>
                      ) : (
                        <>
                          <td colSpan={labelCols} style={{ padding: `${top} 8px ${bottom} 0`, whiteSpace: 'nowrap', ...rowStyle }}>{row.label}</td>
                          <td style={{ padding: `${top} ${trailingCols ? '8px' : '0'} ${bottom} 0`, ...rowStyle, ...(row.valueStyle || {}) }}>{row.value}</td>
                        </>
                      )}
                      {trailingCols > 0 && <td colSpan={trailingCols} />}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* A Prepay Customer pays before anything ships (issue #333). */}
          <PaymentDetailsBox doc={order} companySettings={companySettings} />
        </>
      )}
    </ReportShell>
  );
}

export default SalesOrderReportView;
